/**
 * QSignalGrid - MT5-style Signal Grid for Q AI Coach
 *
 * Responsive CSS grid container for QSignalGridCards.
 * Alternative view to QSignalTable for dashboard display.
 *
 * Layout (responsive):
 * - Desktop XL: 3 columns
 * - Desktop LG: 2 columns
 * - Tablet/Mobile: 1 column
 *
 * Features:
 * - Category filtering (All, Forex, Metals, Indices)
 * - View toggle between Grid and Table
 * - Symbol visibility management
 * - Real-time data updates
 *
 * @version 1.0.0
 * @phase Phase 1 - Q.AI Coach MT5-style polish
 */

import React, { memo, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Chip, IconButton, Tooltip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import TuneIcon from '@mui/icons-material/Tune';
import QSignalGridCard from './QSignalGridCard';
import { symbolsMatch, pairToQuantX } from '../../utils/symbolMapper';
import {
    COLORS,
    TYPOGRAPHY,
    SPACING,
    RADIUS,
    SHARED_STYLES
} from './signalBoardTheme';

/**
 * Categorize symbol by type
 */
const categorizeSymbol = (symbol) => {
    if (!symbol) return 'forex';
    const upper = symbol.toUpperCase().trim();
    if (upper.includes('/')) return 'pairs';
    if (upper.includes('XAU') || upper.includes('XAG') || upper === 'GOLD' || upper === 'SILVER') return 'metals';
    if (upper.includes('US30') || upper.includes('US500') || upper.includes('NAS') || upper.includes('DAX')) return 'indices';
    if (upper.includes('BTC') || upper.includes('ETH') || upper.includes('SOL')) return 'crypto';
    return 'forex';
};

/**
 * Category definitions
 */
const CATEGORIES = [
    { id: 'all', label: 'All' },
    { id: 'forex', label: 'Forex' },
    { id: 'metals', label: 'Metals' },
    { id: 'crypto', label: 'Crypto' },
    { id: 'indices', label: 'Indices' }
];

/**
 * QSignalGrid Component
 */
const QSignalGrid = memo(({
    signals = [],
    onAskQ,
    viewMode = 'grid',
    onViewModeChange,
    showViewToggle = true,
    showCategories = true,
    // Phase 1B: Enhanced Signal Grid - QuantX data for selected symbol
    quantXData = null,
    selectedSymbol = null,
    // FIX: Add availableSymbols to show all symbols, not just those with signal data
    availableSymbols = []
}) => {
    const [activeCategory, setActiveCategory] = useState('all');
    const [hiddenSymbols, setHiddenSymbols] = useState(() => {
        try {
            const saved = localStorage.getItem('qai_hidden_symbols');
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });

    // FIX v2: Merge signals with available symbols - DEDUP using QuantX mapping
    // Signal Board sends cross-pairs (GOLD/SILVER), availableSymbols has QuantX (XAUUSD_ct)
    // Same logic as QSignalList for consistency
    const allSymbols = useMemo(() => {
        const signalMap = new Map();
        const coveredQuantX = new Set();

        // Add signals first (they have score/verdict data)
        signals.forEach(s => {
            signalMap.set(s.symbol, s);
            const quantx = pairToQuantX(s.symbol);
            if (quantx) {
                coveredQuantX.add(quantx);
            }
        });

        // Add available symbols that aren't already covered by signals
        availableSymbols.forEach(sym => {
            const quantx = pairToQuantX(sym);
            if (!signalMap.has(sym) && !coveredQuantX.has(quantx)) {
                signalMap.set(sym, {
                    symbol: sym,
                    qSignal: 'NEUTRAL',
                    qScore: null,
                    _noSignalData: true
                });
                coveredQuantX.add(quantx);
            }
        });

        return Array.from(signalMap.values());
    }, [signals, availableSymbols]);

    // Calculate category counts - FIX: Use allSymbols instead of just signals
    const categoryCounts = useMemo(() => {
        const counts = { all: 0, forex: 0, metals: 0, crypto: 0, indices: 0, pairs: 0 };
        allSymbols.forEach(s => {
            if (!hiddenSymbols.includes(s.symbol)) {
                counts.all++;
                const cat = categorizeSymbol(s.symbol);
                if (counts[cat] !== undefined) {
                    counts[cat]++;
                }
            }
        });
        return counts;
    }, [allSymbols, hiddenSymbols]);

    // Filter signals by category - FIX: Use allSymbols instead of just signals
    const filteredSignals = useMemo(() => {
        let filtered = allSymbols.filter(s => !hiddenSymbols.includes(s.symbol));
        if (activeCategory !== 'all') {
            filtered = filtered.filter(s => categorizeSymbol(s.symbol) === activeCategory);
        }
        return filtered;
    }, [allSymbols, activeCategory, hiddenSymbols]);

    // Handle category change
    const handleCategoryChange = useCallback((categoryId) => {
        setActiveCategory(categoryId);
    }, []);

    // Handle view mode change
    const handleViewModeChange = useCallback((event, newMode) => {
        if (newMode !== null && onViewModeChange) {
            onViewModeChange(newMode);
        }
    }, [onViewModeChange]);

    // Empty state
    if (!signals || signals.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: SPACING.xl,
                    minHeight: '200px'
                }}
            >
                <Typography sx={{ color: COLORS.textMuted, fontSize: '14px' }}>
                    Waiting for signal data...
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                gap: SPACING.sm
            }}
        >
            {/* Header: Categories + View Toggle */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: SPACING.sm,
                    paddingBottom: SPACING.sm,
                    borderBottom: `1px solid ${COLORS.borderDefault}`
                }}
            >
                {/* Categories */}
                {showCategories && (
                    <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {CATEGORIES.filter(cat => categoryCounts[cat.id] > 0 || cat.id === 'all').map(cat => (
                            <Chip
                                key={cat.id}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <span>{cat.label}</span>
                                        <Box
                                            component="span"
                                            sx={{
                                                fontSize: '10px',
                                                fontWeight: 700,
                                                backgroundColor: activeCategory === cat.id
                                                    ? COLORS.signalBuyBg
                                                    : 'rgba(255, 255, 255, 0.1)',
                                                color: activeCategory === cat.id
                                                    ? COLORS.signalBuy
                                                    : COLORS.textMuted,
                                                padding: '1px 5px',
                                                borderRadius: '4px',
                                                minWidth: '18px',
                                                textAlign: 'center'
                                            }}
                                        >
                                            {categoryCounts[cat.id]}
                                        </Box>
                                    </Box>
                                }
                                size="small"
                                onClick={() => handleCategoryChange(cat.id)}
                                sx={{
                                    backgroundColor: activeCategory === cat.id
                                        ? COLORS.bgCard
                                        : 'transparent',
                                    color: activeCategory === cat.id
                                        ? COLORS.textPrimary
                                        : COLORS.textMuted,
                                    border: `1px solid ${activeCategory === cat.id
                                        ? COLORS.borderHover
                                        : 'transparent'}`,
                                    cursor: 'pointer',
                                    fontWeight: activeCategory === cat.id ? 600 : 500,
                                    fontSize: '12px',
                                    height: '28px',
                                    transition: 'all 0.15s ease',
                                    '&:hover': {
                                        backgroundColor: COLORS.bgHover
                                    },
                                    '& .MuiChip-label': {
                                        padding: '0 8px'
                                    }
                                }}
                            />
                        ))}
                    </Box>
                )}

                {/* View Toggle */}
                {showViewToggle && onViewModeChange && (
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={handleViewModeChange}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                color: COLORS.textMuted,
                                borderColor: COLORS.borderDefault,
                                padding: '4px 10px',
                                '&:hover': {
                                    backgroundColor: COLORS.bgHover
                                },
                                '&.Mui-selected': {
                                    color: COLORS.signalBuy,
                                    backgroundColor: COLORS.signalBuyBg,
                                    borderColor: COLORS.signalBuyBorder,
                                    '&:hover': {
                                        backgroundColor: 'rgba(39, 181, 109, 0.15)'
                                    }
                                }
                            }
                        }}
                    >
                        <ToggleButton value="grid">
                            <Tooltip title="Grid View">
                                <GridViewIcon sx={{ fontSize: '16px' }} />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="table">
                            <Tooltip title="Table View">
                                <TableRowsIcon sx={{ fontSize: '16px' }} />
                            </Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>
                )}
            </Box>

            {/* Signal Grid */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    ...SHARED_STYLES.scrollbar
                }}
            >
                {filteredSignals.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: SPACING.xl,
                            minHeight: '150px'
                        }}
                    >
                        <Typography sx={{ color: COLORS.textMuted, fontSize: '13px' }}>
                            No signals in this category
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: SPACING.md, paddingBottom: SPACING.md }}>
                        {/* ═══════════════════════════════════════════════════════════════════
                            SELECTED CARD - Full Width Expanded View
                            Shows QuantX data for selected symbol, with or without signal match
                            ═══════════════════════════════════════════════════════════════════ */}
                        {(() => {
                            // Try to find matching signal from Signal Board
                            const selectedSignal = filteredSignals.find(s => symbolsMatch(s.symbol, selectedSymbol));

                            // Create a fallback signal object if no match found
                            // This allows displaying QuantX data even without Signal Board data
                            const displaySignal = selectedSignal || {
                                symbol: selectedSymbol,
                                qSignal: 'NEUTRAL',
                                qScore: null,
                                // Mark as QuantX-only (no signal board data)
                                _quantXOnly: true
                            };

                            // Always render card if we have a selectedSymbol
                            if (selectedSymbol) {
                                return (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            borderRadius: RADIUS.md,
                                            border: `2px solid ${selectedSignal ? COLORS.signalBuyBorder : COLORS.borderDefault}`,
                                            backgroundColor: selectedSignal ? 'rgba(39, 181, 109, 0.03)' : 'rgba(255, 255, 255, 0.02)',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <QSignalGridCard
                                            signal={displaySignal}
                                            onAskQ={onAskQ}
                                            quantXData={quantXData}
                                            isSelected={true}
                                            isExpanded={true}
                                        />
                                    </Box>
                                );
                            }
                            return null;
                        })()}

                        {/* Phase 1E: Removed "Other Symbols" compact grid - only show selected card */}
                    </Box>
                )}
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: SPACING.xs,
                    borderTop: `1px solid ${COLORS.borderDefault}`
                }}
            >
                <Typography sx={{ fontSize: '11px', color: COLORS.textMuted }}>
                    {filteredSignals.length} signal{filteredSignals.length !== 1 ? 's' : ''}
                    {hiddenSymbols.length > 0 && (
                        <span style={{ color: COLORS.signalSell, marginLeft: '6px' }}>
                            {hiddenSymbols.length} hidden
                        </span>
                    )}
                </Typography>
            </Box>
        </Box>
    );
});

QSignalGrid.displayName = 'QSignalGrid';

QSignalGrid.propTypes = {
    signals: PropTypes.arrayOf(PropTypes.shape({
        symbol: PropTypes.string.isRequired,
        qSignal: PropTypes.string,
        qScore: PropTypes.number
    })),
    onAskQ: PropTypes.func,
    viewMode: PropTypes.oneOf(['grid', 'table']),
    onViewModeChange: PropTypes.func,
    showViewToggle: PropTypes.bool,
    showCategories: PropTypes.bool,
    // Phase 1B: Enhanced Signal Grid
    quantXData: PropTypes.object,
    selectedSymbol: PropTypes.string,
    // FIX: Available symbols to merge with signals
    availableSymbols: PropTypes.arrayOf(PropTypes.string)
};

export default QSignalGrid;
